import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import {
  API_KEY_EXPIRY_OPTIONS,
  DEFAULT_API_KEY_EXPIRY_DAYS,
  apiKeyService,
} from "@/services/apiKeyService";
import { ApiKey } from "@/types/organization";
import { Copy, Key, Loader2, PlusCircle, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../../supabase/supabase";

const DAY_MS = 86_400_000;
const EXPIRING_SOON_DAYS = 7;

type KeyStatus = { label: string; variant: "default" | "secondary" | "destructive" | "outline" };

function keyStatus(key: ApiKey): KeyStatus {
  if (key.revoked) return { label: "Revoked", variant: "outline" };
  if (!key.expires_at) return { label: "Active", variant: "default" };
  const daysLeft = Math.ceil((new Date(key.expires_at).getTime() - Date.now()) / DAY_MS);
  if (daysLeft <= 0) return { label: "Expired", variant: "destructive" };
  if (daysLeft <= EXPIRING_SOON_DAYS) {
    return { label: `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`, variant: "secondary" };
  }
  return { label: "Active", variant: "default" };
}

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : "—");

export default function ApiKeysTab() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  // "never" or a number of days, as the <select> value
  const [newKeyExpiry, setNewKeyExpiry] = useState(String(DEFAULT_API_KEY_EXPIRY_DAYS));
  const [isCreating, setIsCreating] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  // Owners, admins and platform admins create and revoke keys; members only see them
  const [canManage, setCanManage] = useState(false);

  useEffect(() => {
    if (organizationId) {
      fetchApiKeys();
      checkCanManage();
    }
  }, [organizationId]);

  const checkCanManage = async () => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId || !organizationId) return;
    const [{ data: membership }, { data: isPlatformAdmin }] = await Promise.all([
      supabase
        .from("organization_members")
        .select("role")
        .eq("user_id", userId)
        .eq("organization_id", organizationId)
        .maybeSingle(),
      supabase.rpc("is_platform_admin"),
    ]);
    setCanManage(
      isPlatformAdmin === true || ["owner", "admin"].includes(membership?.role ?? ""),
    );
  };

  const fetchApiKeys = async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      setApiKeys(await apiKeyService.getApiKeys(organizationId));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!organizationId || !newKeyName.trim()) return;
    setIsCreating(true);
    try {
      const expiresInDays = newKeyExpiry === "never" ? null : Number(newKeyExpiry);
      const created = await apiKeyService.createApiKey(organizationId, newKeyName.trim(), expiresInDays);
      setIsCreateDialogOpen(false);
      setNewlyCreatedKey(created.key);
      setNewKeyName("");
      setNewKeyExpiry(String(DEFAULT_API_KEY_EXPIRY_DAYS));
      await fetchApiKeys();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create API key",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    try {
      await apiKeyService.revokeApiKey(keyId);
      toast({ title: "API key revoked", description: "It stops working immediately." });
      await fetchApiKeys();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to revoke API key",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "API key copied to clipboard" });
  };

  if (isLoading && apiKeys.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Keys for calling the Voxi API (send as the <code>x-api-key</code> header). A key can
            only act in this organization.
          </CardDescription>
        </div>
        {canManage && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4" />
                Create API Key
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New API Key</DialogTitle>
                <DialogDescription>
                  Name it after where it will be used, e.g. the client's production server.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="api-key-name">API Key Name</Label>
                  <Input
                    id="api-key-name"
                    placeholder="e.g., Production Server"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="api-key-expiry">Expires</Label>
                  <select
                    id="api-key-expiry"
                    className="w-full p-2 border border-input rounded-md bg-background"
                    value={newKeyExpiry}
                    onChange={(e) => setNewKeyExpiry(e.target.value)}
                  >
                    {API_KEY_EXPIRY_OPTIONS.map((option) => (
                      <option key={option.label} value={option.days === null ? "never" : String(option.days)}>
                        {option.days === null ? "Never" : `In ${option.label}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={isCreating}>
                  Cancel
                </Button>
                <Button onClick={handleCreateKey} disabled={!newKeyName.trim() || isCreating}>
                  {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Key
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {newlyCreatedKey && (
          <Dialog open={!!newlyCreatedKey} onOpenChange={() => setNewlyCreatedKey(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>API Key Created</DialogTitle>
                <DialogDescription>
                  Copy it now: it is shown only once and cannot be retrieved later.
                </DialogDescription>
              </DialogHeader>
              <div
                id="created-api-key"
                className="bg-muted p-3 rounded-md font-mono text-sm break-all"
              >
                {newlyCreatedKey}
              </div>
              <DialogFooter>
                <Button variant="outline" className="gap-2" onClick={() => copyToClipboard(newlyCreatedKey)}>
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
                <Button onClick={() => setNewlyCreatedKey(null)}>Done</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {apiKeys.length === 0 ? (
          <div className="text-center py-6">
            <Key className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
            <h3 className="text-lg font-medium">No API Keys</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {canManage
                ? "You haven't created any API keys yet."
                : "Owners and admins can create API keys for this organization."}
            </p>
            {canManage && (
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(true)} className="mx-auto">
                Create your first API key
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-6 gap-4 py-2 px-4 font-medium text-sm text-muted-foreground">
              <div className="col-span-2">Name</div>
              <div>Created</div>
              <div>Expires</div>
              <div>Last used</div>
              <div className="text-right">Status</div>
            </div>
            {apiKeys.map((key) => {
              const status = keyStatus(key);
              return (
                <div
                  key={key.id}
                  data-api-key-row={key.key_prefix}
                  className="grid grid-cols-6 gap-4 py-3 px-4 rounded-lg border items-center"
                >
                  <div className="col-span-2">
                    <p className="font-medium">{key.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{key.key_prefix}…</p>
                  </div>
                  <div className="text-sm text-muted-foreground">{formatDate(key.created_at)}</div>
                  <div className="text-sm text-muted-foreground">
                    {key.expires_at ? formatDate(key.expires_at) : "Never"}
                  </div>
                  <div className="text-sm text-muted-foreground">{formatDate(key.last_used_at)}</div>
                  <div className="flex items-center justify-end gap-2">
                    <Badge variant={status.variant}>{status.label}</Badge>
                    {canManage && !key.revoked && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            title="Revoke"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
                            <AlertDialogDescription>
                              "{key.name}" stops working immediately. Anything still using it will
                              get "Invalid or expired API key". This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRevokeKey(key.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Revoke Key
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
